import React, { useRef, useContext, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react"; // Framer Motion
import useOnClickOutside from "../hooks/useOnClickOutside";
import ModalContext from "../context/ModalContext";
import ScrollBlocker from "react-scroll-blocker";
import classNames from "classnames";

const transition = {
    opacity: { duration: 0.2, ease: "linear" },
    default: { duration: 0.2, ease: "ease" },
};

const Modal = ({ children, className, hideClose, alignTop, ...props }) => {
    const modalRef = useRef();
    const buttonRef = useRef();
    const modalContext = useContext(ModalContext);
    useOnClickOutside(modalRef, () => !hideClose && modalContext.hideModal());

    function hideModal() {
        if (hideClose) return;
        if (props.onHide) props.onHide();
        modalContext.hideModal();
    }

    const handleKeypress = (event) => {
        if (event.key === "Escape") {
            hideModal();
        }
    };

    function trapFocus(modal) {
        let tabables = getTabables();

        modal.addEventListener("keydown", handleKeydown, true);
        if (modal.getAttribute("data-trap") === "true") {
            modal.removeEventListener("keydown", handleKeydown, true);
        }
        modal.setAttribute("data-trap", true);

        function handleKeydown(e) {
            tabables = getTabables();
            const posInArr = tabables.indexOf(e.target);

            if (
                e.keyCode === 9 &&
                !e.shiftKey &&
                posInArr === tabables.length - 1
            ) {
                e.preventDefault();
                tabables[0].focus();
            } else if (e.keyCode === 9 && e.shiftKey && posInArr === 0) {
                e.preventDefault();
                tabables[tabables.length - 1].focus();
            }
        }

        function getTabables() {
            return Array.from(
                modal.querySelectorAll(
                    'a, button, input:not([type="hidden"]), textarea, select, details, [tabindex]:not([tabindex="-1"])'
                )
            ).filter(
                (el) =>
                    el.offsetParent !== null &&
                    el.offsetWidth > 0 &&
                    el.offsetHeight > 0
            );
        }
    }

    useEffect(() => {
        document.addEventListener("keyup", handleKeypress);
        return () => document.removeEventListener("keyup", handleKeypress);
    }, []);

    useEffect(() => {
        if (modalRef.current) {
            trapFocus(modalRef.current);
            modalRef.current.focus();
        }
    }, []);

    const classes = classNames("modal", className, {
        "modal--no-title": !props.title,
    });

    const overlayClasses = classNames("modal-overlay", className, {
        "modal-overlay--align-top": alignTop,
    });

    return (
        <AnimatePresence>
            {modalContext.visible && (
                <>
                    <ScrollBlocker />

                    <motion.div
                        className={overlayClasses}
                        initial={{ opacity: 0, pointerEvents: "none" }}
                        animate={{ opacity: 1, pointerEvents: "auto" }}
                        exit={{ opacity: 0, pointerEvents: "none" }}
                        transition={{ duration: 0.2, ease: "linear" }}
                        key="modal-overlay"
                    >
                        <div>
                            <motion.div
                                role="dialog"
                                aria-modal
                                aria-labelledby={props.title}
                                aria-describedby="modal-content"
                                ref={modalRef}
                                className={classes}
                                tabIndex={0}
                                initial={{ opacity: 0, y: "5vh" }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: "5vh" }}
                                transition={transition}
                            >
                                {props.title && (
                                    <h2
                                        className="modal__title h3"
                                        id="modal-title"
                                    >
                                        {props.title}
                                    </h2>
                                )}

                                <div id="modal-content">
                                    {modalContext.component}
                                </div>

                                {!hideClose && (
                                    <button
                                        className="reset-button modal__close-button"
                                        type="button"
                                        ref={buttonRef}
                                        aria-label="Luk modal"
                                        onClick={hideModal}
                                    >
                                        <i
                                            className="icon-remove-alt"
                                            aria-hidden
                                        />
                                    </button>
                                )}
                            </motion.div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default Modal;
